import { ReadonlyVec2 } from 'gl-matrix';
import { gl } from '../../Engine';
import { TextureSource } from '../../core/models/TextureSource';
import { RenderStorage } from '../../core/render/RenderStorage';
import { QuadRenderer } from '../../core/render/renderers/QuadRenderer';
import { Environment } from '../../data/Environment';
import { TextureView } from '../TextureView';
import { Filter, FilterClass, newFilter } from './filters/Filter';

export type LayerBitMask = number;

/**
 * {@link Layer} represents a visualization of specific type of data on a {@link TextureView}.
 * T defines the type of Objects that the {@link Layer} visualizes.
 */
export abstract class Layer<T> implements TextureSource {
    /**
     * The {@link Filter} that is used to render data of type {@link T} on this {@link Layer}.
     */
    protected filter: Filter<T>;

    /**
     * The {@link TextureView parent} of this {@link Layer}.
     */
    protected readonly parent: TextureView;

    /**
     * {@link WebGLTexture Texture} containing the data rendered on this {@link Layer}.
     * The size of this {@link WebGLTexture texture} should be equal to the size of the {@link Layer} and the {@link TextureView} it resides on.
     */
    private readonly _texture: WebGLTexture;

    /**
     * Constructor to create a layer.
     * @param view The {@link TextureView parent} of this {@link Layer}. Layers may not exist without TextureViews.
     */
    protected constructor(parent: TextureView, env: Environment) {
        this.parent = parent;
        const texture = gl.createTexture();
        if (!texture) {
            throw new Error('Unable to create LayerTexture');
        }
        this._texture = texture;
        this.filter = newFilter(this.getDefaultFilter(), this);
        const size = parent.size;
        if (size[0] !== 0 && size[1] !== 0) {
            this.resize(size, false, env);
        }
    }

    /**
     * Method to inform the {@link Layer} that the {@link view View}'s {@link TextureView.size size} has changed.\
     * As {@link Layer} should always be the same size as the {@link TextureView View}, we must also update the Layer's {@link size}.
     * After the {@link Layer Layer}'s {@link size} has been updated, it's contents are also updated.
     * @param size {@link TextureView View's} new size in pixels.
     * @returns Reference to this.
     */
    public resize(
        size: ReadonlyVec2,
        updateContent = true,
        env: Environment
    ): Layer<T> {
        gl.bindTexture(gl.TEXTURE_2D, this.texture);
        gl.texImage2D(
            gl.TEXTURE_2D,
            0,
            this.internalFormat,
            size[0],
            size[1],
            0,
            this.format,
            gl.UNSIGNED_BYTE,
            null
        );
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.bindTexture(gl.TEXTURE_2D, null);
        if (this.filter.resize) {
            this.filter.resize(size);
        }
        if (updateContent) {
            this.update(true, env);
        }
        return this;
    }

    public get filterClass(): FilterClass<T> {
        return this.filter.constructor as FilterClass<T>;
    }

    /**
     * Method to get the default filter for this Layer. Layers must always have some filter
     * so a default filter is needed.
     */
    public abstract getDefaultFilter(): FilterClass<T>;

    /**
     * The internal format of the {@link Layer._texture Layer texture}.
     * For valid values see https://registry.khronos.org/OpenGL-Refpages/gl4/html/glTexImage2D.xhtml
     */
    public abstract get internalFormat(): number;

    /**
     * The format of the Layer texture. One of:
     * GL_RED, GL_RG, GL_RGB, GL_BGR, GL_RGBA, GL_BGRA,
     * GL_RED_INTEGER, GL_RG_INTEGER, GL_RGB_INTEGER, GL_BGR_INTEGER, GL_RGBA_INTEGER, GL_BGRA_INTEGER,
     * GL_STENCIL_INDEX, GL_DEPTH_COMPONENT or GL_DEPTH_STENCIL
     */
    public abstract get format(): number;

    /**
     * Every {@link Layer}-class must define their own {@link layerBit}.\
     * LayerBit is used by {@link ViewUpdateEvent ViewUpdateEvents} to select which {@link Layer Layers} need to be updated as a result of the Event.
     */
    public abstract get layerBit(): LayerBitMask;

    /**
     * Abstract method to retrieve all the targets from the {@link Environment}.
     * @param environment {@link Environment} from which the targets are to be retrieved.
     * @returns Array of objects of requested type found in the {@link Environment}.
     */
    public abstract getTargets(environment: Environment): T[];

    /**
     * Method to erase the {@link Layer.texture texture} allocated by this {@link Layer}.\
     * Also {@link Filter.delete deletes} the {@link Filter} this {@link Layer} is using.
     * @returns True to indicate that the {@link Layer} and all of it's contents were deleted\
     * and it's WebGL memory de-allocated.
     */
    public delete(): boolean {
        gl.deleteTexture(this.texture);
        if (this.filter.delete) {
            this.filter.delete();
        }
        return true;
    }

    /**
     * Method to change the {@link Filter} used by this {@link Layer}.
     * @param filterClass The {@link FilterClass} that should be used instead.
     * @returns Reference to this
     */
    public setFilter(filterClass: FilterClass<T>): Layer<T> {
        this.filter = newFilter(filterClass, this);
        return this;
    }

    /**
     * Method to update the {@link Layer.texture Layer's texture}.\
     * This method checks whether the {@link Layer} meets the update conditions of the {@link ViewUpdateEvent}.\
     * If it does, the contents are updated.
     * @param updateContent Boolean flag controlling whether the {@link Layer Layer's} contents need updating.
     * @returns True if the {@link Layer} was updated. False otherwise.
     */
    public update(updateContent: boolean, env: Environment): boolean {
        return this.filter.update(this.getTargets(env), updateContent);
    }

    public renderTexture(
        renderer: QuadRenderer = RenderStorage.getRenderer(QuadRenderer)
    ): void {
        renderer.render(this);
    }

    /**
     * Method to get reference to this {@link Layer}'s parent {@link TextureView}.
     * @returns Reference to this {@link Layer}'s parent.
     */
    public getView(): TextureView {
        return this.parent;
    }

    public get texture(): WebGLTexture {
        return this._texture;
    }
}

/**
 * Type that represents {@link Layer} constructor.
 */
export type LayerClass<T> = new (
    view: TextureView,
    env: Environment
) => Layer<T>;
